import React, { Children, useMemo, useState } from 'react';
import CssHelper from 'Services/Utils/Css/CssHelper';
import styles from './TagGroup.module.sass';

const Tooltip = React.lazy(() => import('Components/Atoms/Tooltip/Tooltip'));

const TagGroup = ({
    children,
    collapse, wrap,

    isCollapsed,
    disableAlphabeticOrder,

    alignRight,
    fullWidth,

    ...props
}: Props & any) => {
    const size = 10;
    const [isOpen, setOpen] = useState(false);

    const handleClick = () => setOpen(!isOpen);
    
    const cssClasses = CssHelper.createCssClass(`
        ${styles['tag-group']}
        ${styles['tag-group__wrap--'+(collapse === 'medium' ? 'wrap' : 'nowrap')]}
        ${alignRight ? styles['tag-group--right'] : styles['tag-group--left']}
        ${fullWidth ? styles['tag-group--fullwidth'] : ''}
    `);

    const orderChildren = (children: any) => {
        if (!disableAlphabeticOrder) {
            return children.sort(
                (a: any, b: any) =>
                    a?.props?.children?.localeCompare(b?.props?.children)
            );
        }

        return children;
    }

    const childrenArrayFiltered = useMemo(() => {
        const childrenArray = orderChildren(Children.toArray(children));

        if (isOpen) return childrenArray;

        if (collapse === 'small') return [childrenArray?.[0]];
        if (collapse === 'medium') return childrenArray.slice(0, size);

        return childrenArray;
    }, [children, collapse, isOpen]);

    const childrenRest = useMemo(() => {
        let childrenRestArray = orderChildren(Children.toArray(children));

        if (collapse === 'small') return childrenRestArray.slice(1);
        if (collapse === 'medium') return childrenRestArray.slice(size);

        return childrenRestArray;
    }, [children, isOpen]);

    const [tooltipRef, setTooltipRef] = useState<any>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    return <div className={cssClasses} {...props}>
        {
            <>
                { childrenArrayFiltered?.map(i => i) }
                {
                    childrenRest.length > 0
                    && <>
                        {
                            collapse === 'small'
                            && <div
                                className={styles['tag-group__more']}
                                ref={setTooltipRef}
                                onMouseOver={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                +{ childrenRest.length }
                                {
                                    showTooltip && <Tooltip ref={tooltipRef}>
                                        <div className={styles['tag-group__more-group']}>
                                            {
                                                childrenRest.map((i: any, idx: number) => {
                                                    return <>{ idx !== 0 ? <div style={{ marginRight: '8px' }}>, </div> : '' }{ i }</>
                                                })
                                            }
                                        </div>
                                    </Tooltip>
                                }
                            </div>
                        }
                        {
                            collapse === 'medium'
                            && <div
                                className={styles['tag-group__more']}
                                onClick={handleClick}
                                style={{ cursor: 'pointer' }}
                            >
                                {
                                    !isOpen
                                    ? `Open +${childrenRest?.length} meer...`
                                    : `Sluiten...`
                                }
                            </div>
                        }
                    </>
                }
            </>
        }
    </div>;
}

interface Props {
    collapse?: 'small' | 'medium' | 'large',
    wrap?: 'wrap' | 'nowrap'
    children?: any,

    isCollapsed?: any,
    disableAlphabeticOrder?: boolean,
    alignRight?: any,
    fullWidth?: any
}

export default React.memo(TagGroup);